import React from 'react';
import { VisibilityTrackerContainer } from '@noths/polaris-client-components';
import { colors, Hyperlink, IconHelp, Tooltip } from '@noths/polaris-client-ribbons-design-system';

import { Partner, ReviewPagination, Reviews } from 'src/types';
import { getImagePath } from 'src/utils';
import {
  NO_REVIEWS,
  PRODUCT_REVIEWS_TITLE,
  REVIEW_TOOLTIP_BUTTON,
  REVIEW_TOOLTIP_CONTENT,
} from '../constants/copy';
import { PRODUCT_REVIEWS_VISIBILITY_NAME } from '../tracking/events';
import { DispatchProps } from '../types';
import { Pagination } from './Pagination';
import { ProductReview } from './ProductReview';
import * as styles from '../styles/ProductReviews.styles';

export interface ProductReviewsProps extends DispatchProps {
  reviews: Reviews;
  partner: Partner;
  loadPageOfReviews: (page: number) => void;
}

const feefoLogoPath = getImagePath('feefoLogo.png');

const canShowPaginationTypeGuard = (
  pagination: ReviewPagination | null,
): pagination is ReviewPagination => (pagination?.totalPages ?? 0) > 1;

export const ProductReviews = ({
  loadPageOfReviews,
  partner,
  reviews: { items, pagination },
  onProductReviewsExpandReadMore,
  onProductReviewsNextPage,
  onProductReviewsPagination,
  onProductReviewsProductRatingSystem,
}: ProductReviewsProps) => (
  <VisibilityTrackerContainer sectionName={PRODUCT_REVIEWS_VISIBILITY_NAME}>
    <div>
      <h2 css={styles.heading}>{PRODUCT_REVIEWS_TITLE}</h2>
      {items.length === 0 ? (
        <p css={styles.noReviews}>{NO_REVIEWS}</p>
      ) : (
        <>
          <ul css={styles.reviewsList}>
            {items.map((review, index) => (
              <li key={`product-review-${review.id}`}>
                <ProductReview
                  review={review}
                  partner={partner}
                  onExpandReviewClick={onProductReviewsExpandReadMore}
                />
                {index !== items.length - 1 && <hr css={styles.divider} />}
              </li>
            ))}
          </ul>
          {canShowPaginationTypeGuard(pagination) && (
            <Pagination
              pagination={pagination}
              loadPageOfReviews={loadPageOfReviews}
              onProductReviewsNextPage={onProductReviewsNextPage}
              onProductReviewsPagination={onProductReviewsPagination}
            />
          )}

          <img css={styles.feefoLogo} src={feefoLogoPath} alt="Feefo" />
          <div css={styles.tooltipWrapper}>
            <Tooltip alignment="left" content={REVIEW_TOOLTIP_CONTENT}>
              {({ onClick }) => (
                <Hyperlink
                  href="/rating_help.html"
                  role="button"
                  rel="nofollow"
                  onClick={(e) => {
                    onClick(e);

                    onProductReviewsProductRatingSystem();
                  }}
                >
                  <span css={styles.tooltipInfo}>
                    {REVIEW_TOOLTIP_BUTTON}
                    <IconHelp
                      css={styles.tooltipInfoIcon}
                      width={16}
                      height={16}
                      color={colors.brandBluePrimary}
                    />
                  </span>
                </Hyperlink>
              )}
            </Tooltip>
          </div>
        </>
      )}
    </div>
  </VisibilityTrackerContainer>
);
